import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, IconButton, Link, Text } from "@chakra-ui/react";
import { Alert } from "@/components";
import { DownloadButton } from "@/components/DownloadButton";
import { useCharge } from "@/hooks";
import { formatDate, getColorBadgeStatus, getInvoiceStatus, showToast } from "@/utils";
import { ShareIcon } from "@/styles/icons/ShareIcon";
import { Button, ColumnDefinition, Pills, Table } from "barte-design-system";
import { InvoiceHistoryCharge } from "@/models";

const EMPTY_INVOICE = {
  uuidInvoice: "empty",
  invoiceNumber: null,
  status: "NOT_EMITTED",
  createdAt: null,
  invoiceImageLink: null,
  invoiceDownloadLink: null,
} as unknown as InvoiceHistoryCharge;

const InvoiceTable = () => {
  const [isShareAlertOpen, setIsShareAlertOpen] = useState(false);
  const [invoiceModalState, setInvoiceModalState] = useState(false);
  const [cancelModalState, setCancelModalState] = useState(false);
  const [emitAgainModalState, setEmitAgainModalState] = useState(false);
  const [selectedUuidInvoice, setSelectedUuidInvoice] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    sendInvoiceEmail,
    cancelInvoice,
    emitInvoice,
    getChargeData,
    getChargeInvoicesData,
    charge,
    invoices,
  } = useCharge();
  const shareAlertRef = useRef();
  const { id } = useParams();
  const ref = useRef();

  const hasEmittedInvoice =
    invoices?.length &&
    invoices?.findIndex(
      (invoice) =>
        invoice.status === "AUTHORIZED" ||
        invoice.status === "SEND_INVOICE_SYSTEM"
    );

  useEffect(() => {
    if (!id) return;
    getChargeInvoicesData(id);
    getChargeData(String(id));
  }, [id]);

  async function handleEmitInvoice() {
    try {
      await emitInvoice(id);
      showToast({ type: "success", message: "Emissão realizada com sucesso." });
    } catch (error) {
      showToast({
        type: "error",
        message: "Não foi possível emitir sua nota fiscal.",
      });
    } finally {
      setInvoiceModalState(false);
      setEmitAgainModalState(false);
      getChargeData(String(id));
      getChargeInvoicesData(id);
    }
  }

  async function handleCancelInvoice() {
    try {
      setIsLoading(true);
      await cancelInvoice(selectedUuidInvoice, charge?.cnpjSeller);
      showToast({ message: "Nota fiscal cancelada com sucesso!", type: "success" });
    } catch (error) {
      error?.response?.data?.errors.map((itemError) => {
        showToast({
          message: `${itemError?.description} - ${itemError?.code}`,
          type: "error",
        });
      });
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      setIsLoading(false);
      getChargeInvoicesData(id);
      getChargeData(String(id));
      setSelectedUuidInvoice("");
      setCancelModalState(false);
    }
  }

  async function handleSendEmail() {
    try {
      await sendInvoiceEmail(id);
      showToast({ message: "E-mail enviado com sucesso!", type: "success" });
    } catch (error) {
      error?.response?.data?.errors.map((itemError) => {
        showToast({
          message: `${itemError?.description} - ${itemError?.code}`,
          type: "error",
        });
      });
    } finally {
      setIsShareAlertOpen(false);
    }
  }

  const tableData: InvoiceHistoryCharge[] =
    invoices?.length > 0 ? invoices : [EMPTY_INVOICE];

  const columns: ColumnDefinition<InvoiceHistoryCharge>[] = [
    {
      key: "invoiceNumber",
      title: "Número da NF",
      dataIndex: "invoiceNumber",
      render: (v) => v ?? "-",
    },
    {
      key: "createdAt",
      title: "Data de Emissão",
      dataIndex: "createdAt",
      render: (v) => (v ? formatDate(v as string) : "-"),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (v) =>
        v ? (
          <Pills
            label={getInvoiceStatus(v as string)}
            variant="light"
            state={getColorBadgeStatus(v as string)}
            size="sm"
          />
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      title: "Ações",
      dataIndex: "status",
      render: (v, record) =>
        v === "AUTHORIZED" ? (
          <Button
            variant="outline"
            onClick={() => {
              setSelectedUuidInvoice(record.uuidInvoice);
              setCancelModalState(true);
            }}
          >
            Cancelar
          </Button>
        ) : null,
    },
    {
      key: "downloads",
      title: "",
      dataIndex: "invoiceDownloadLink",
      render: (v, record) => {
        const links = v as { pdfImageLink?: string; xmlImageLink?: string } | null;
        return (
        <Flex gap="2">
          <DownloadButton disabled={!links?.pdfImageLink && !links?.xmlImageLink}>
            {links?.pdfImageLink && (
              <Link
                w="100%"
                textAlign="center"
                _hover={{ borderRadius: "5px", bgColor: "gray.50" }}
                href={links.pdfImageLink}
                target="_blank"
              >
                PDF
              </Link>
            )}
            {links?.xmlImageLink && (
              <Link
                w="100%"
                textAlign="center"
                _hover={{ borderRadius: "5px", bgColor: "gray.50" }}
                href={links.xmlImageLink}
                target="_blank"
              >
                XML
              </Link>
            )}
          </DownloadButton>
          <IconButton
            bgColor="transparent"
            aria-label="Compartilhar com e-mail"
            disabled={record.status !== "AUTHORIZED"}
            w="30px"
            h="35px"
            icon={<ShareIcon opacity="0.45" />}
            onClick={() => setIsShareAlertOpen(true)}
          />
        </Flex>
        );
      },
    },
  ];

  return (
    <>
      <Alert
        closeButtonText="Não"
        confirmButtonText="Sim"
        isOpen={isShareAlertOpen}
        onConfirm={handleSendEmail}
        onClose={() => setIsShareAlertOpen(!isShareAlertOpen)}
        title="Enviar Nota Fiscal ao cliente por email?"
        leastDestructiveRef={shareAlertRef}
      />
      <Alert
        isOpen={invoiceModalState}
        title="Enviar Nota Fiscal antecipadamente para esse cliente?"
        messageBody="A NF não será enviada novamente após o pagamento da cobrança"
        leastDestructiveRef={ref}
        closeButtonText="Cancelar"
        confirmButtonText="Sim"
        onClose={() => setInvoiceModalState(false)}
        onConfirm={handleEmitInvoice}
      />
      <Alert
        isOpen={emitAgainModalState}
        title="Emitir novamente a nota fiscal?"
        messageBody="A NF será emitida novamente!"
        leastDestructiveRef={ref}
        closeButtonText="Cancelar"
        confirmButtonText="Sim"
        onClose={() => setEmitAgainModalState(false)}
        onConfirm={handleEmitInvoice}
      />
      <Alert
        isOpen={cancelModalState}
        title="Cancelar Nota Fiscal para esse cliente?"
        messageBody="A NF será cancelada logo após a confirmação"
        leastDestructiveRef={ref}
        closeButtonText="Cancelar"
        confirmButtonText="Sim"
        onClose={() => setCancelModalState(false)}
        onConfirm={handleCancelInvoice}
        isLoading={isLoading}
      />

      <Flex direction="column" gap="4" w="full">
        <Flex align="center" justify="space-between">
          <Text fontWeight="bold" fontSize="16px" color="#0f172a">
            Nota Fiscal
          </Text>
          {invoices?.length === 0 && (
            <Button
              variant="outline"
              onClick={() => setInvoiceModalState(true)}
              disabled={!charge?.activeInvoiceConfig}
            >
              Adiantar emissão de NF
            </Button>
          )}
          {invoices?.length > 0 && hasEmittedInvoice === -1 && (
            <Button
              variant="outline"
              onClick={() => setEmitAgainModalState(true)}
            >
              Emitir Novamente
            </Button>
          )}
        </Flex>
        <Table
          data={tableData}
          columns={columns}
          rowKey={(r) => r.uuidInvoice}
        />
      </Flex>
    </>
  );
};

export { InvoiceTable };
